import type { ReactNode } from 'react';
import React, { createContext, useContext, useMemo } from 'react';
import { RelayEnvironmentProvider, useLazyLoadQuery } from 'react-relay';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import SpaceNavigationQueryArtifact, {
	type SpaceNavigationQuery,
	type SpaceNavigationQuery$data,
	type SpaceNavigationQuery$variables,
} from './__generated__/SpaceNavigationQuery.graphql.ts';

type SpaceNavigationAffordanceData = {
	spaceFragmentRef: NonNullable<SpaceNavigationQuery$data['space']>;
	licenseFragmentRef: SpaceNavigationQuery$data['license'];
	suggestionCountFragmentRef: SpaceNavigationQuery$data['spaceCurationSuggestionCount'];
	isUserExternalCollaborator: boolean;
};

const SpaceNavigationAffordanceDataContext = createContext<
	SpaceNavigationAffordanceData | undefined
>(undefined);

export const useSpaceNavigationAffordanceData = (): SpaceNavigationAffordanceData | undefined =>
	useContext(SpaceNavigationAffordanceDataContext);

const SpaceNavigationAffordanceDataProviderInner = ({
	children,
	spaceKey,
	variables,
}: {
	children: ReactNode;
	spaceKey: string;
	variables?: SpaceNavigationQuery$variables;
}): React.JSX.Element => {
	const { cloudId, edition, isLicensed, isLoggedIn } = useSessionData();
	const hasLicensedAccess = useIsLicensed();
	const includePersistentInviteLicense =
		edition !== ConfluenceEdition.FREE &&
		isLoggedIn &&
		(!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_license_error_fix')
			? hasLicensedAccess
			: isLicensed);

	const routeData = useLazyLoadQuery<SpaceNavigationQuery>(
		SpaceNavigationQueryArtifact,
		variables ?? {
			cloudId,
			spaceKey,
			isLicensed,
			includePersistentInviteLicense,
			includeSpaceCurationCount: false,
			includeSpaceViewsPersistence: false,
		},
		{ fetchPolicy: 'store-only' },
	);
	const affordanceData = useMemo<SpaceNavigationAffordanceData | undefined>(() => {
		if (!routeData.space) {
			return undefined;
		}

		return {
			spaceFragmentRef: routeData.space,
			licenseFragmentRef: routeData.license,
			suggestionCountFragmentRef: routeData.spaceCurationSuggestionCount,
			isUserExternalCollaborator: routeData.user?.confluence?.permissionType === 'EXTERNAL',
		};
	}, [
		routeData.license,
		routeData.space,
		routeData.spaceCurationSuggestionCount,
		routeData.user?.confluence?.permissionType,
	]);

	if (!affordanceData) {
		return <>{children}</>;
	}

	return (
		<SpaceNavigationAffordanceDataContext.Provider value={affordanceData}>
			{children}
		</SpaceNavigationAffordanceDataContext.Provider>
	);
};

export const SpaceNavigationAffordanceDataProvider = ({
	children,
	spaceKey,
	variables,
}: {
	children: ReactNode;
	spaceKey: string;
	variables?: SpaceNavigationQuery$variables;
}): React.JSX.Element => (
	<RelayEnvironmentProvider environment={getRelayEnvironment()}>
		<SpaceNavigationAffordanceDataProviderInner spaceKey={spaceKey} variables={variables}>
			{children}
		</SpaceNavigationAffordanceDataProviderInner>
	</RelayEnvironmentProvider>
);
